import React, { useEffect, useRef } from 'react';
import { useReducedMotion } from '../../hooks/useReducedMotion';

/**
 * ParallaxScene — Décor superposé de blobs colorés qui répondent
 * au scroll (vertical) ET à la position de la souris.
 *
 * - Chaque couche est une div .parallax-blob positionnée en absolute.
 * - `depth` (0.02–0.15 conseillé) contrôle l'amplitude du parallaxe.
 * - Rendu neutralisé sous prefers-reduced-motion.
 *
 * Usage :
 *   <ParallaxScene className="absolute inset-0">
 *     <ParallaxLayer depth={0.06} className="parallax-blob parallax-blob-cyan"
 *                    style={{ top: '10%', left: '15%', width: 200, height: 200 }} />
 *   </ParallaxScene>
 */
export function ParallaxScene({ className = '', children, ...rest }) {
  const ref = useRef(null);
  const reduced = useReducedMotion();

  useEffect(() => {
    if (reduced) return;
    const node = ref.current;
    if (!node) return;

    let mx = 0, my = 0, sy = 0, raf = 0;

    const apply = () => {
      raf = 0;
      const layers = node.querySelectorAll('[data-parallax-depth]');
      layers.forEach((el) => {
        const d = parseFloat(el.getAttribute('data-parallax-depth') || '0.05');
        const tx = (mx * d).toFixed(2);
        const ty = ((my * d) + (sy * d * 0.6)).toFixed(2);
        el.style.transform = `translate3d(${tx}px, ${ty}px, 0)`;
      });
    };
    const schedule = () => { if (!raf) raf = requestAnimationFrame(apply); };

    const onMouse = (e) => {
      const r = node.getBoundingClientRect();
      mx = e.clientX - (r.left + r.width / 2);
      my = e.clientY - (r.top + r.height / 2);
      schedule();
    };
    const onScroll = () => {
      sy = window.scrollY || 0;
      schedule();
    };

    node.addEventListener('mousemove', onMouse);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      node.removeEventListener('mousemove', onMouse);
      window.removeEventListener('scroll', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [reduced]);

  return (
    <div ref={ref} className={`parallax-scene ${className}`} {...rest}>
      {children}
    </div>
  );
}

export function ParallaxLayer({ depth = 0.06, className = '', style, ...rest }) {
  return (
    <div
      data-parallax-depth={depth}
      className={className}
      style={style}
      {...rest}
    />
  );
}
